import { Pipe, PipeTransform } from "@angular/core";
import * as DOMPurify from "dompurify";
import { DomSanitizer } from "@angular/platform-browser";
import { TranslateService } from "@ngx-translate/core";

@Pipe({ name: "filesize" })
export class FileSize implements PipeTransform {
  transform(value) {
    let size = parseInt(value);
    if (size > 1000 && size < 1000000) {
      size = size / 1000;
      return size.toFixed(2) + "kb";
    } else if (size > 1000000) {
      size = size / 1000000;
      return size.toFixed(2) + "mb";
    } else return size + "b";
  }
}
@Pipe({ name: "safe" })
export class SafePipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}
  transform(url) {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }
}

@Pipe({ name: "safeHtml" })
export class SafeHTML {
  constructor(private sanitizer: DomSanitizer) {}

  transform(html) {
    if (!html || html === "null" || html === "undefined") {
      html = "";
    }
    DOMPurify.addHook("afterSanitizeAttributes", function (node) {
      // set all elements owning target to target=_blank
      if ("target" in node) {
        node.setAttribute("target", "_blank");
        node.setAttribute("rel", "noopener noreferrer");
      }
    });
    let sanitized = DOMPurify.sanitize(html, {
      ADD_TAGS: ["iframe"],
      ADD_ATTR: [
        "allow",
        "allowfullscreen",
        "frameborder",
        "scrolling",
        "target",
      ],
    });
    return this.sanitizer.bypassSecurityTrustHtml(sanitized);
  }
}

@Pipe({ name: "safeStyle" })
export class SafeStyles {
  constructor(private sanitizer: DomSanitizer) {}

  transform(html) {
    return this.sanitizer.bypassSecurityTrustStyle(html);
  }
}

@Pipe({ name: "filter" })
export class TextFilter {
  transform(arr, key, value) {
    return arr.filter((i) => i[key] == value);
  }
}
@Pipe({ name: "truncate" })
export class Truncate {
  transform(text: string, length) {
    return text.substr(0, parseInt(length)) + "..";
  }
}

@Pipe({ name: "formatText" })
export class FormatText {
  transform(text: string) {
    if (!text) return "";
    return text.replace(/(?:\\r\\n|\\r|\\n)/g, "<br>");
  }
}

@Pipe({ name: "friendlyType" })
export class FriendlyType {
  types: Object = {
    roles: "Role",
    resources: "Resource",
    gradecategories: "Grade Category",
    quizquestions: "Quiz Question",
    dashboard: "Dashboard",
    files: "File",
    objectives: "Objective",
    quizbanks: "Quiz Bank",
    gradeschemas: "Grade Schema",
    gradecolumns: "Grade Column",
    announcements: "Announcement",
    groupsets: "Group Set",
    groups: "Group",
    lti: "Tool",
    assignmentgroups: "Assignment Group",
    exceptionlogentry: "Exception Log Entry",
    learningcomponent: "Learning Component",
    quizanswers: "Quiz Answer",
    quizgroups: "Quiz Group",
    gradeentry: "Grade Entry",
    gradescales: "Gradescale",
    rubrics: "Rubric",
    filefolders: "File Folder",
    test: "Test",
    moduleitems: "Moduleitem",
    outcomes: "Outcome",
    assignments: "Assignment",
    discussions: "Discussion",
    modules: "Module",
    users: "User",
    courses: "Courses",
    pages: "Page",
    quizzes: "Quizzes",
  };

  constructor() {}

  toTitleCase(str) {
    return str.replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  }
  transform(text: string) {
    if (!text) return "";
    if (this.types.hasOwnProperty(text)) {
      text = this.types[text];
    } else {
      text = this.toTitleCase(text?.replace(/__c/, ""));
    }
    return text;
  }
}

@Pipe({ name: "translationFromString", pure: false })
export class TranslationFromStringPipe implements PipeTransform {
  constructor(private translateService: TranslateService) {}

  capitalizeEachWord(input) {
    if (typeof input !== "string") return input;
    const words = input.split(" ");
    const capitalizedWords = words.map(
      (word) => word.charAt(0).toUpperCase() + word.slice(1).toLocaleLowerCase()
    );
    return capitalizedWords.join(" ");
  }

  transform(input = "") {
    if (input === undefined) input = "";
    const pattern =
      /\[\s*([^|\]]*?)\s*\|\s*translation\s*(?:\|\s*([^|\]]*?)\s*)?]/g;
    return !input.replace
      ? input
      : input.replace(pattern, (_, key, additionalFormatArgs) => {
          const trimmedKey = key.trim();
          let content = this.translateService.instant(trimmedKey);
          if (!content) return trimmedKey;
          additionalFormatArgs = additionalFormatArgs?.trim();
          if (additionalFormatArgs === "uppercase")
            content = content.toUpperCase();
          if (additionalFormatArgs === "lowercase")
            content = content.toLowerCase();
          if (additionalFormatArgs === "capitalize")
            content = this.capitalizeEachWord(content);
          return content;
        });
  }
}

@Pipe({ name: "translation", pure: false })
export class TranslationPipe implements PipeTransform {
  constructor(private translateService: TranslateService) {}

  transform(key): any {
    if (typeof key === "number" && !isNaN(key)) {
      return key;
    }
    if (!key || key === "undefined") return "";
    let content = "";
    try {
      content = this.translateService.instant(key);
    } catch (error) {
      console.log("error in translation pipe: " + error);
    }
    return content || key;
  }
}

@Pipe({ name: "transformText", pure: false })
export class TransformTextPipe implements PipeTransform {
  constructor(private translateService: TranslateService) {}

  capitalizeEachWord(str) {
    const words = str.split(" ");
    const capitalizedWords = words.map(
      (word) => word.charAt(0).toUpperCase() + word.slice(1).toLocaleLowerCase()
    );
    return capitalizedWords.join(" ");
  }

  transform(text, type): any {
    if (typeof text === "number" && !isNaN(text)) {
      return text;
    }
    return !text || typeof text !== "string" || text === "undefined"
      ? ""
      : type === "uppercase"
      ? text.toUpperCase()
      : type === "lowercase"
      ? text.toLowerCase()
      : type === "capitalize"
      ? this.capitalizeEachWord(text)
      : text;
  }
}

@Pipe({
  name: "showFalsy",
})
export class ShowFalsyPipe implements PipeTransform {
  transform(value: any): string {
    if (value === 0) return "0";
    if (value === null) return "null";
    if (value === false) return "false";
    if (value === undefined) return "";
    return value;
  }
}

@Pipe({
  name: "highlight",
})
export class HighlightPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(value: string, args: string[]): any {
    if (!value) return value;
    if (!args || args?.length === 0) return value;

    const escapedArgs = args.map(arg => arg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const pattern = escapedArgs.map(word => `(?<=\\s|^)['"]?\\s*${word}\\s*['"]?(?=\\s|$)`).join('|');
    const re = new RegExp(pattern, 'gi');
    const text = value.replace(re, (match) => `<span class="highlight">${match}</span>`);
    return this.sanitizer.bypassSecurityTrustHtml(text);
  }
}
